import { ComboboxList } from '../../../../../components/general/ComboboxList.tsx';
import { createTable, RenderingPresetType, TableHeaderSpec } from '../../../../../components/table/CreateTable.tsx';
import { Client } from '../../Client.ts';
import { cleanDocumentFileName } from '../../../../../lib/TFICLib.ts';
import { DocumentActivityStatusEnum } from '../../../../../types/enums';
import RelativeLoadingFrame from '../../../../../components/RelativeLoadingFrame.tsx';

export interface DocumentActivityRow {
    id: string;
    file_name: string;
    created_at: string;
    shared_via: string;
    status: string;
}

interface DocumentActivityTabProps {
    clients: Client[];
    selectedClient: Client | null;
    onClientSelect: (client: Client | null) => void;
    documents: DocumentActivityRow[];
    isLoadingDocuments?: boolean;
}

export function DocumentActivityTab({
    clients,
    selectedClient,
    onClientSelect,
    documents,
    isLoadingDocuments = false,
}: DocumentActivityTabProps) {
    const clientMapper = (client: Client) => ({
        ...client,
        displayName: client.name,
        id: client.id,
    });

    const tableSpec: TableHeaderSpec<DocumentActivityRow> = {
        title: `${documents.length} Documenten`,
        columns: [
            {
                name: 'Bestandsnaam',
                field: 'file_name',
                customRender: (doc: DocumentActivityRow) => {
                    return (
                        <div className="text-sm text-gray-900">
                            {cleanDocumentFileName(doc.file_name)}
                        </div>
                    );
                },
            },
            {
                name: 'Datum',
                field: 'created_at',
                renderingPreset: RenderingPresetType.Date,
            },
            {
                name: 'Gedeeld via',
                field: 'shared_via',
            },
            {
                name: 'Status',
                field: 'status',
                customRender: (doc: DocumentActivityRow) => {
                    if (doc.status === DocumentActivityStatusEnum.DUPLICATE) {
                        return (
                            <span className="inline-flex items-center rounded-full px-3 text-[10px] font-bold bg-yellow-200 text-gray-900">
                                Duplicaat
                            </span>
                        );
                    }
                    if (doc.status === DocumentActivityStatusEnum.IMPORTED) {
                        return (
                            <span className="inline-flex items-center rounded-full px-3 text-[10px] font-bold text-black">
                                Geïmporteerd
                            </span>
                        );
                    }
                    if (doc.status === DocumentActivityStatusEnum.PROCESSING) {
                        return (
                            <span className="inline-flex items-center rounded-full px-3 text-[10px] font-bold bg-sky-200 text-gray-900">
                                In verwerking
                            </span>
                        );
                    }
                    return <div className="text-sm text-gray-900">{doc.status || '-'}</div>;
                },
            },
        ],
        onEmptyRender: () => (
            <p className="text-gray-500 pt-12 text-center">
                Geen documenten gevonden voor deze klant.
            </p>
        ),
        options: {
            disableTopPaginationWidget: false,
            disableBottomPaginationWidget: true,
            hideTableHeader: false,
        },
    };

    const tableData = documents.map(doc => ({ ...doc, id: doc.id }));
    const TableComponent = createTable<DocumentActivityRow>(tableData, tableSpec);

    return (
        <div>
            <h3 className="text-xl text-gray-900 font-semibold py-3 pt-6">
                Document activiteit
            </h3>
            <p className="text-sm text-gray-600 mb-4">
                Op dit tabblad zie je wat er gebeurt met documenten die je klanten hebben gedeeld. Duplicaat houdt in dat het document al in het systeem stond en daarom is verborgen. In verwerking betekent dat ons systeem het document momenteel verwerkt. Het document verschijnt in je overzicht zodra de verwerking is afgerond.
            </p>
            <div className="mt-4 mb-6">
                <ComboboxList
                    label="Selecteer een klant"
                    options={clients}
                    onSelect={(client) => onClientSelect(client as Client | null)}
                    selected={selectedClient || null}
                    emptySelectionMessage="Selecteer een klant..."
                    internalMapper={clientMapper}
                    showClearButton={true}
                />
            </div>
            {selectedClient && (
                <div className="mt-6">
                    <RelativeLoadingFrame isLoading={isLoadingDocuments}>
                        {TableComponent()}
                    </RelativeLoadingFrame>
                </div>
            )}
            {!selectedClient && (
                <p className="text-sm text-gray-600 italic mt-4">
                    Selecteer een klant om documenten te bekijken
                </p>
            )}
        </div>
    );
}

